import React, { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQRContext } from '../context/QRContext';
import useUrlValidation from '../hooks/useUrlValidation';
import useScrollToSection from '../hooks/useScrollToSection';

const HeroContainer = styled.div`
  min-height: 100vh;
  min-height: 100dvh; /* Use dynamic viewport height for mobile browsers */
  background: linear-gradient(135deg, #0f172a 0%, #581c87 50%, #0f172a 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 1rem;
    min-height: 90vh; /* Slightly smaller on mobile to account for browser UI */
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 100%;
  padding: 0 1rem;
  text-align: center;
  box-sizing: border-box;

  @media (min-width: 480px) {
    max-width: 500px;
    padding: 0;
  }
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 10vw, 6rem);
  font-weight: 900;
  margin: 0 0 1rem;
  line-height: 1.1;
  color: white;
  text-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  width: 100%;

  @media (max-width: 768px) {
    font-size: clamp(2.5rem, 8vw, 4rem);
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #d1d5db;
  margin-bottom: 4rem;
  line-height: 1.6;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1.125rem;
    margin-bottom: 3rem;
  }
`;

const InputForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const URLInput = styled.input`
  width: 100%;
  padding: 1.5rem 2rem;
  font-size: 16px; /* Prevents iOS zoom on focus */
  min-height: 60px; /* Ensures touch target is large enough */
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.95);
  color: #1f2937;
  transition: all 0.3s ease;
  font-weight: 500;
  -webkit-text-size-adjust: 100%; /* Prevent text zoom on iOS */
  -ms-text-size-adjust: 100%;
  -moz-text-size-adjust: 100%;

  &:focus {
    outline: none;
    border-color: #7c3aed;
    background: white;
    box-shadow: 0 0 0 4px rgba(124, 58, 237, 0.15);
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.7);
    cursor: not-allowed;
  }
`;

const CreateButton = styled.button`
  background: #ec4899;
  color: white;
  font-weight: 700;
  font-size: 1.125rem;
  padding: 1.5rem 3rem;
  border: none;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(124, 58, 237, 0.4);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(124, 58, 237, 0.5);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const LoadingSpinner = styled.svg`
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const FloatingButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 50px;
  padding: 1rem 2rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  margin-top: 5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }

  svg {
    animation: float 3s ease-in-out infinite;
  }

  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-3px);
    }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  padding: 1rem;
  color: #fca5a5;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  backdrop-filter: blur(10px);
`;

const HeroComponent = () => {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const { setUrl: setContextUrl, setContentType, resetAll } = useQRContext();
  const { validateUrl } = useUrlValidation();
  const { scrollToSection } = useScrollToSection();

  const handleCreateQR = useCallback(
    async (e) => {
      e.preventDefault();
      setError('');

      try {
        const processedUrl = validateUrl(url);
        setIsLoading(true);

        // Simulate processing time for better UX
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Reset context and set new URL
        resetAll();
        setContentType('url');
        setContextUrl(processedUrl);

        // Navigate to QR generator page
        navigate('/qr');
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    },
    [url, validateUrl, resetAll, setContentType, setContextUrl, navigate],
  );

  const handleInputChange = useCallback(
    (e) => {
      setUrl(e.target.value);
      if (error) {
        setError(''); // Clear error when user starts typing
      }
    },
    [error],
  );

  const handleScrollToFeatures = useCallback(() => {
    scrollToSection('how');
  }, [scrollToSection]);

  const isFormValid = url.trim().length > 0;

  return (
    <HeroContainer id="home">
      <ContentWrapper>
        <Title>Qraft Your Code</Title>
        <Subtitle>
          Simple, fast QR code generation. Create your code instantly.
        </Subtitle>

        <InputForm onSubmit={handleCreateQR}>
          <URLInput
            type="text"
            placeholder="Enter your URL here (e.g., example.com)..."
            value={url}
            onChange={handleInputChange}
            disabled={isLoading}
            autoComplete="url"
            autoFocus
          />

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <CreateButton type="submit" disabled={isLoading || !isFormValid}>
            {isLoading ? (
              <>
                <LoadingSpinner
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeOpacity="0.3"
                  />
                  <path
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </LoadingSpinner>
                Creating...
              </>
            ) : (
              'Create QR Code'
            )}
          </CreateButton>
        </InputForm>

        <FloatingButton onClick={handleScrollToFeatures}>
          Explore Features
          <ChevronDown size={16} />
        </FloatingButton>
      </ContentWrapper>
    </HeroContainer>
  );
};

export default HeroComponent;
